<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { useInbox } from 'dashboard/composables/useInbox';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  isTelegramChannel,
  contentAttributes,
  sender,
  isFirstInGroup,
} = useMessageContext();
const { t } = useI18n();
const { inbox } = useInbox();   // chat-account inbox — labels our own outgoing

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

// Telegram sender label, shown ONCE at the top of each sender-run (matching the
// avatar, which renders once per run). Lives here (not in TextBubble) so it sits
// ABOVE the reply-quote and applies to every bubble type. Sources, in order:
//   • group/channel author — MTProto object OR Bot string (telegram-only attrs)
//   • DM / our own outgoing — the message's own sender, but only in Telegram
//     conversations (so email/web/Captain bubbles never sprout a name).
const senderLabel = computed(() => {
  const ca = contentAttributes.value || {};
  const structured =
    ca.telegramGroupSender?.name ||
    ca.telegram_group_sender?.name ||
    ca.telegramSenderName ||
    ca.telegram_sender_name;
  // GROUP / channel messages carry a PER-MESSAGE sender → label EVERY bubble so
  // each message is attributed to its real author (all incoming share the same
  // Chatwoot sender = the account, so the run-grouping would otherwise hide it
  // and a different member's message reads as the previous one).
  if (structured) return structured;
  // DM / our own outgoing: the sender is constant, so only label the first of a
  // sender-run (repeating the contact's name on every bubble would be noise).
  if (!isFirstInGroup.value) return '';
  if (!isTelegramChannel.value) return '';
  // Our own outgoing: label with the CHAT-ACCOUNT name (the Telegram identity we
  // send as, e.g. "Sven"), NOT the internal Chatwoot agent ("Administrator").
  if (orientation.value === ORIENTATION.RIGHT) return inbox.value?.name || '';
  return sender.value?.name || '';   // incoming → the contact's name
});

// Per-sender colour for the group label — Telegram-style: each member gets a
// stable, distinct hue (hashed from the sender id/name). Only for group senders;
// DM / own-outgoing keep the default warm orange.
const SENDER_COLORS = [
  '#e1565c', '#3892e0', '#e08a3c', '#7d57c2', '#d14a9e',
  '#1a9c8e', '#c79a1e', '#5b8c2a', '#cf5a3a', '#4767c4',
];
const senderColor = computed(() => {
  const ca = contentAttributes.value || {};
  const g = ca.telegramGroupSender || ca.telegram_group_sender;
  const key = String(g?.id ?? g?.name ?? ca.telegramSenderName ?? ca.telegram_sender_name ?? '');
  if (!key) return '';   // DM / own → no per-user colour (label falls back to orange)
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return SENDER_COLORS[h % SENDER_COLORS.length];
});

// Author of the QUOTED message, shown inside the reply-quote box (Telegram-style
// "replying to <name>"). Same source precedence as senderLabel.
const quotedSender = computed(() => {
  if (!isTelegramChannel.value) return '';
  const m = inReplyTo?.value;
  if (!m) return '';
  const ca = m.contentAttributes || m.content_attributes || {};
  return (
    ca.telegramGroupSender?.name ||
    ca.telegram_group_sender?.name ||
    ca.telegramSenderName ||
    ca.telegram_sender_name ||
    m.sender?.name ||
    ''
  );
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <!-- Telegram sender label — warm orange, once per sender-run, ABOVE the
         reply-quote so it reads name → quote → message (Telegram style). -->
    <div
      v-if="senderLabel"
      class="text-xs font-semibold leading-tight mb-0"
      :style="{ color: senderColor || '#ea580c' }"
    >
      {{ senderLabel }}
    </div>
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 rounded-lg cursor-pointer bg-n-alpha-black1"
      :class="isTelegramChannel ? 'mb-1' : 'mb-2'"
      @click="scrollToMessage"
    >
      <!-- Author of the quoted message (Telegram-style "replying to <name>"). -->
      <div
        v-if="quotedSender"
        class="text-xs font-semibold leading-tight mb-0.5"
        style="color: #ea580c"
      >
        {{ quotedSender }}
      </div>
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
        isTelegramChannel ? 'mt-0.5' : 'mt-2',
      ]"
    />
  </div>
</template>
